// Drives the cart drawer's reservation countdown timer and free-shipping
// progress bar (cart-drawer-incentives.liquid). Liquid renders the correct
// initial state from the cart at page-render time; this file ticks the
// timer down every second and re-fetches /cart.js on cart changes so the
// bar animates smoothly without a full drawer re-render.
import { ThemeEvents } from '@theme/events';

const TIMER_STORAGE_KEY = 'cart-reservation-started-at';
const TIMER_DURATION_MS = 5 * 60 * 1000;

/** @param {number} ms */
function formatTimer(ms) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function getReservationStart() {
  const stored = sessionStorage.getItem(TIMER_STORAGE_KEY);
  const storedTime = stored ? Number(stored) : NaN;
  if (!Number.isNaN(storedTime) && Date.now() - storedTime < TIMER_DURATION_MS) {
    return storedTime;
  }
  const now = Date.now();
  sessionStorage.setItem(TIMER_STORAGE_KEY, String(now));
  return now;
}

function tickTimers() {
  const timerEls = document.querySelectorAll('[data-cart-timer]');
  if (timerEls.length === 0) return;

  const startedAt = getReservationStart();
  const remaining = startedAt + TIMER_DURATION_MS - Date.now();
  const display = remaining <= 0 ? formatTimer(TIMER_DURATION_MS) : formatTimer(remaining);

  if (remaining <= 0) {
    sessionStorage.removeItem(TIMER_STORAGE_KEY);
  }

  for (const el of timerEls) {
    el.textContent = display;
  }
}

/** @param {number} cents */
function formatMoney(cents) {
  return new Intl.NumberFormat('et-EE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

/** @param {{total_price: number}} cart */
function updateShippingProgress(cart) {
  const root = document.querySelector('[data-cart-incentives]');
  if (!(root instanceof HTMLElement)) return;

  const threshold = Number(root.dataset.freeShippingThreshold) || 3000;
  const total = cart.total_price ?? 0;
  const progress = Math.min(100, total > 0 ? (total * 100) / threshold : 0);
  const remaining = Math.max(0, threshold - total);
  const unlocked = total >= threshold;

  const fill = root.querySelector('[data-shipping-fill]');
  const badge = root.querySelector('[data-shipping-badge]');
  if (fill instanceof HTMLElement) {
    fill.style.width = `calc(clamp(16px, ${progress}%, calc(100% - 16px)) + 16px)`;
  }
  if (badge instanceof HTMLElement) {
    badge.style.left = `clamp(16px, ${progress}%, calc(100% - 16px))`;
  }

  const text = root.querySelector('[data-shipping-text]');
  if (text instanceof HTMLElement) {
    text.innerHTML = unlocked
      ? 'Palju õnne! Said tasuta saatmise 🎉'
      : `Osta veel <span>${formatMoney(remaining)}</span> eest tasuta saatmiseks!`;
  }
}

async function refreshFromCart() {
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    updateShippingProgress(cart);
  } catch (error) {
    console.warn('[cart-drawer-incentives] Failed to refresh cart totals:', error);
  }
}

document.addEventListener(ThemeEvents.cartLinesUpdate, () => {
  refreshFromCart();
});

tickTimers();
setInterval(tickTimers, 1000);
